import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import {
    Checkbox,
    IconChevronUp,
    IconChevronDown,
    Text,
} from '@zillow/constellation';

const propTypes = {
    columnConfig: PropTypes.arrayOf(PropTypes.shape({
        width: PropTypes.number.isRequired,
        dataKey: PropTypes.string,
        header: PropTypes.string.isRequired,
        isSortable: PropTypes.bool,
        renderer: PropTypes.func,
        align: PropTypes.string,
        className: PropTypes.string,
    })),
    data: PropTypes.object,
    isSelected: PropTypes.bool,
    isExpanded: PropTypes.bool,
    canFilter: PropTypes.bool,
    canSelectRow: PropTypes.bool,
    canExpandCurrentRow: PropTypes.bool,
    onToggleSelected: PropTypes.func,
    onToggleExpanded: PropTypes.func,
    ExpandedComponent: PropTypes.func,
    columnDivisions: PropTypes.number.isRequired,
    selectionDisabled: PropTypes.bool.isRequired,
    rowSelectionDisabled: PropTypes.func.isRequired,
    hasExpandableRows: PropTypes.bool.isRequired,
};

const defaultProps = {
    columnConfig: [],
    data: {},
    isSelected: false,
    isExpanded: false,
    canFilter: false,
    canSelectRow: false,
    canExpandCurrentRow: false,
    onToggleSelected: () => { },
    onToggleExpanded: () => { },
    ExpandedComponent: () => { },
};

const TableMFHRow = ({
    columnConfig,
    data,
    isSelected,
    isExpanded,
    canFilter,
    canSelectRow,
    canExpandCurrentRow,
    onToggleSelected,
    onToggleExpanded,
    ExpandedComponent,
    columnDivisions,
    selectionDisabled,
    rowSelectionDisabled,
    hasExpandableRows,
    edgePadding,
}) => {
    const formatValue = col => (col.renderer ? col.renderer(data) : data[col.dataKey]);
    const formatColWidth = width => `${width / columnDivisions * 100}%`;

    const showEmptyColumn = (hasExpandableRows || canFilter) && !canExpandCurrentRow;
    return (
        <React.Fragment>
            <div
                style={{
                    paddingLeft: edgePadding,
                    paddingRight: edgePadding,
                }}
                className={cx('TableMFH-data-row', {
                    'TableMFH-selected': isSelected,
                    'TableMFH-expanded': isExpanded,
                })}
                data-test="row"
            >
                {canSelectRow && (
                    <div
                        className="TableMFH-col"
                        style={{ width: formatColWidth(1) }}
                    >
                        <div className="TableMFH-icon-wrapper">
                            <Checkbox
                                checked={isSelected}
                                onChange={onToggleSelected}
                                disabled={selectionDisabled || rowSelectionDisabled(data)}
                            />
                        </div>
                    </div>
                )}
                {columnConfig.map((col, i) => (
                    <div
                        key={col.dataKey || i}
                        style={{ width: formatColWidth(col.width) }}
                        className={cx(
                            col.className,
                            'TableMFH-col',
                            {
                                'TableMFH-align-center': col.align === 'center',
                            },
                            {
                                'TableMFH-align-right': col.align === 'right',
                            }
                        )}
                    >
                        <Text fontType="finePrint">
                            {formatValue(col)}
                        </Text>
                    </div>
                ))}
                {canExpandCurrentRow && (
                    <div className="TableMFH-col" style={{ width: formatColWidth(1) }}>
                        <div className="TableMFH-icon-wrapper" onClick={onToggleExpanded}>
                            {isExpanded
                                ? <IconChevronUp className="TableMFH-expand-icon" size="xs" />
                                : <IconChevronDown className="TableMFH-expand-icon" size="xs" />
                            }
                        </div>
                    </div>
                )}
                {showEmptyColumn && <div className="TableMFH-col" style={{ width: formatColWidth(1) }} />}
            </div>
            {canExpandCurrentRow && isExpanded ?
                (
                    <div className="TableMFH-expanded TableMFH-expanded-content">
                        {ExpandedComponent(data)}
                    </div>
                )
                : null}
        </React.Fragment>
    );
};

TableMFHRow.propTypes = propTypes;
TableMFHRow.defaultProps = defaultProps;

export default TableMFHRow;
